import axiosInstance from "@/utils/axiosInstance";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { buildQueryString } from "@/utils/buildQueryString";
import {
	AddFavoritesPayload,
	AddFavoritesRes,
	CountryQuizResult,
	EligibilityCoursesResults,
	EligibilityDetailRes,
	EligibilityPayload,
	EligibilityUniversityResults,
	getFavoritesRes,
	InstitutePayload,
	InstituteResponse,
	ProgramResponse,
	UpdateFavoritesCardsRes,
} from "@/store/types/aiCourseFinder";
import { startLoading, stopLoading } from "../loader/loaderSlice";

export const getInstitutesResults = createAsyncThunk<
	InstituteResponse,
	InstitutePayload
>(
	"aiCourseFinder/getInstitutesResults",
	async (payload, { rejectWithValue, dispatch }) => {
		dispatch(startLoading());
		try {
			const res = await axiosInstance.get(
				`search/course/country/${payload.id}?search=${buildQueryString(
					payload.search,
				)}&filter=${buildQueryString(
					payload.filter ?? {},
				)}&sort=${buildQueryString(payload.sort ?? {})}`,
			);

			dispatch(stopLoading());
			return {
				list: res.data?.data,
				total: res.data.total_count,
				status: true,
				all_course_level: res.data?.all_course_level,
				total_pages: res.data?.total_pages,
				all_course_specialisation: res.data?.all_course_specialisation,
				all_course_duration: res.data?.all_course_duration,
				all_course_stream: res.data?.total_pages,
				college_specialisation_category:
					res.data?.college_specialisation_category,
			};
		} catch (e) {
			dispatch(stopLoading());
			return rejectWithValue(e);
		}
	},
);

export const getProgramsResults = createAsyncThunk<
	ProgramResponse,
	InstitutePayload
>(
	"aiCourseFinder/getProgramsResults",
	async (payload, { rejectWithValue, dispatch }) => {
		dispatch(startLoading());

		try {
			const res = await axiosInstance.get(
				`search/course/country/${payload.id}?search=${buildQueryString(
					payload.search,
				)}&filter=${buildQueryString(
					payload.filter ?? {},
				)}&sort=${buildQueryString(payload.sort ?? {})}`,
			);
			dispatch(stopLoading());

			return {
				list: res.data?.data,
				total: res.data.total_count,
				status: true,
			};
		} catch (e) {
			dispatch(stopLoading());

			return rejectWithValue(e);
		}
	},
);

export const getEligibilityCoursesResults = createAsyncThunk<
	EligibilityCoursesResults,
	InstitutePayload
>(
	"aiCourseFinder/getEligibilityCoursesResults",
	async (payload, { rejectWithValue, dispatch }) => {
		dispatch(startLoading());

		try {
			const res = await axiosInstance.get(
				`search/course/country/${payload.id}?search=${buildQueryString(
					payload.search,
				)}&filter=${buildQueryString(
					payload.filter ?? {},
				)}&sort=${buildQueryString(payload.sort ?? {})}`,
			);
			dispatch(stopLoading());
			return {
				list: res.data?.data,
				total: res.data.total_count,
				status: true,
			};
		} catch (e) {
			dispatch(stopLoading());

			return rejectWithValue(e);
		}
	},
);

export const getEligibilityUniversity = createAsyncThunk<
	EligibilityUniversityResults,
	InstitutePayload
>(
	"aiCourseFinder/getEligibilityUniversity",
	async (payload, { rejectWithValue, dispatch }) => {
		dispatch(startLoading());

		try {
			const res = await axiosInstance.get(
				`search/course/country/${payload.id}?search=${buildQueryString(
					payload.search,
				)}&filter=${buildQueryString(
					payload.filter ?? {},
				)}&sort=${buildQueryString(payload.sort ?? {})}`,
			);

			dispatch(stopLoading());

			return {
				list: res.data?.data,
				total: res.data.total_count,
				status: true,
			};
		} catch (e) {
			dispatch(stopLoading());

			return rejectWithValue(e);
		}
	},
);

export const getGlobalSearchFilter = createAsyncThunk(
	"aiCourseFinder/getGlobalSearchFilter",
	async (payload, { rejectWithValue, dispatch }) => {
		dispatch(startLoading());
		try {
			const res = await axiosInstance.get(`search/global/filters`);
			dispatch(stopLoading());

			return {
				course_levels: res.data?.course_levels,
				college_specialisations: res.data?.college_specialisations,
				durations: res.data?.durations,
				course_specialisations: res.data?.course_specialisations,
				course_streams: res.data?.course_streams,
				course_intake_details: res.data?.course_intake_details,
				course_names: res.data?.course_names,
			};
		} catch (e) {
			dispatch(stopLoading());

			return rejectWithValue(e);
		}
	},
);

export const addFavoritesCards = createAsyncThunk<
	AddFavoritesRes,
	AddFavoritesPayload
>("aiCourseFinder/addFavorites", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await axiosInstance.post(
			`/students/add/favourite`,
			payload,
		);
		return { status: true, message: data.message };
	} catch (e) {
		return rejectWithValue(e);
	}
});

export const getFavoritesCards = createAsyncThunk<getFavoritesRes, string>(
	"aiCourseFinder/getFavoritesCards",
	async (payload, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.get(
				`/students/get/favourites/${payload}`,
				{
					cacheTTL: 0,
				},
			);

			return {
				colleges: res.data?.colleges || [],
				courses: res.data?.courses || [],
				status: true,
			};
		} catch (e) {
			return rejectWithValue(e);
		}
	},
);

export const removeFavoritesCards = createAsyncThunk<
	UpdateFavoritesCardsRes,
	{ userId: string; favouriteId: string }
>(
	"aiCourseFinder/removeFavoritesCards",
	async ({ userId, favouriteId }, { rejectWithValue, dispatch }) => {
		dispatch(startLoading());
		try {
			const res = await axiosInstance.patch(
				`students/remove/favourites/${userId}`,
				{
					favouriteId,
				},
			);
			dispatch(stopLoading());
			return { status: res.data.status, message: res.data.message };
		} catch (e) {
			dispatch(stopLoading());
			return rejectWithValue(e);
		}
	},
);

export const saveEligibilityDetails = createAsyncThunk<
	EligibilityDetailRes,
	EligibilityPayload
>(
	"aiCourseFinder/saveEligibilityDetails",
	async (payload, { rejectWithValue, dispatch }) => {
		dispatch(startLoading());
		try {
			const res = await axiosInstance.post(`students/add/eligibility`, payload);
			dispatch(stopLoading());

			return { data: res.data.data };
		} catch (e) {
			dispatch(stopLoading());
			return rejectWithValue(e);
		}
	},
);

export const getEligibilityDetails = createAsyncThunk<
	EligibilityDetailRes,
	string
>(
	"aiCourseFinder/getEligibilityDetails",
	async (userId, { rejectWithValue, dispatch }) => {
		dispatch(startLoading());
		try {
			const { data: res } = await axiosInstance.get(
				`students/get/eligibility/${userId}`,
			);

			dispatch(stopLoading());
			return { data: res.data };
		} catch (e) {
			dispatch(stopLoading());
			return rejectWithValue(e);
		}
	},
);

export const submitCountryQuiz = createAsyncThunk<
	CountryQuizResult,
	{
		responses: { question: string; answer: string }[];
		studentProfile: Record<string, unknown>;
	}
>(
	"aiCourseFinder/submitCountryQuiz",
	async (payload, { rejectWithValue, dispatch }) => {
		dispatch(startLoading());
		try {
			const response = await axiosInstance.post(
				"/search/country-recommendation",
				payload,
				{ apiVersion: "v2" },
			);
			dispatch(stopLoading());
			return response.data?.data ?? response.data ?? {};
		} catch (e) {
			dispatch(stopLoading());
			return rejectWithValue(e);
		}
	},
);
